/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — pure step-progression and required-field rules for the booking form
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      -
 *   siblings:  booking-calendar.js, booking-people.js, booking-filters.js, booking-request.js, booking-validation.js
 *   exports:   contactFieldRequirements, peopleFieldRequirements, isStep1Done,
 *              isStep2Done, productSkipsOnlinePayment, effectiveBookingSettings
 *   tests:     none
 *
 * Pure step-progression and required-field rules carved out of
 * controllers/index.js. No `this`: every input is an explicit parameter.
 * contactFieldRequirements/peopleFieldRequirements derive which fields are
 * mandatory from booking_settings; isStep1Done/isStep2Done gate the two-step
 * operator/product selection depending on operatoreFirst.
 */

// Which contact fields the booking form must enforce. `mobile` is always
// required; payment_required implies both cod_fiscale and a billing address.
export function contactFieldRequirements(bookingSettings) {
  const settings = bookingSettings || {};
  const contact = settings.field_required?.contact || {};

  return {
    mobile: true,
    data_nascita: contact.data_nascita || false,
    luogo_nascita: contact.luogo_nascita || false,
    cod_fiscale: (contact.cod_fiscale || settings.payment_required) || false,
    codice_destinatario: contact.codice_destinatario || false,
    addresses: (contact.addresses || settings.payment_required) || false
  };
}

// A product configured as "paid on site" (option_json.skip_online_payment)
// never opens an online payment, whatever the tenant-wide
// booking_settings.payment_required says. dispos_data ships the product's raw
// `option` JSON string, so the flag is known before the price call.
export function productSkipsOnlinePayment(product) {
  const option = product?.option;

  if (!option) {
    return false;
  }

  try {
    return JSON.parse(option)?.skip_online_payment === true;
  } catch (error) {
    // A malformed `option` must not break the booking: fall back to the
    // tenant-wide setting.
    return false;
  }
}

// booking_settings with payment_required narrowed by the selected product, so
// every payment-driven rule (required fields, invoice data, summary copy)
// reads one effective value instead of the raw tenant setting.
export function effectiveBookingSettings(bookingSettings, skipOnlinePayment) {
  const settings = bookingSettings || {};

  return Object.assign({}, settings, {
    payment_required: !!settings.payment_required && !skipOnlinePayment
  });
}

// Which participant fields the booking form must enforce.
export function peopleFieldRequirements(bookingSettings) {
  const settings = bookingSettings || {};
  const people = settings.field_required?.people || {};

  return {
    mobile: people.mobile || false,
    data_nascita: people.data_nascita || false,
    luogo_nascita: people.luogo_nascita || false,
    cod_fiscale: people.cod_fiscale || false
  };
}

// Step 1 is satisfied once the first of the two selections (operator or
// product, depending on operatoreFirst) has been made or is configured away.
export function isStep1Done({
  contactSelected,
  productSelected,
  operatoreFirst,
  step0,
  skipStep0,
  skipContact,
  skipProduct
}) {
  if (contactSelected && productSelected) {
    return true;
  }

  if (!step0 && !skipStep0) {
    return false;
  }

  if (operatoreFirst) {
    return contactSelected || skipContact;
  }

  return productSelected || skipProduct;
}

// Step 2 is satisfied once the second of the two selections has been made or is
// configured away. It can never be done before step 1.
export function isStep2Done({
  contactSelected,
  productSelected,
  operatoreFirst,
  step1,
  skipContact,
  skipProduct
}) {
  if (!step1) {
    return false;
  }

  if (operatoreFirst) {
    return productSelected || skipProduct;
  }

  return contactSelected || skipContact;
}
